import { Trans, useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import axios from "axios";
import { getApiPath } from "../../utils/apiSettings";
import { formatCurrency } from "../../utils/utils";

const defaultPricingData = {
  noColor: [],
  oneColor: [],
  twoColors: [],
  moldFees: [],
  edges: [],
};
const isMobile = window.innerWidth < 640;

/**
 * PricingTable component that fetches and displays pricing data.
 */
export function PricingTable() {
  const { t } = useTranslation();

  const [pricingData, setPricingData] = useState(defaultPricingData);
  const [fetchStatus, setFetchStatus] = useState("");

  // Initial request to fetch pricing table data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setFetchStatus("loading");

        const request = await axios.get(getApiPath() + "/pricing-table");
        const result = request.data;

        if (result?.data) {
          setPricingData({
            noColor: result.data.no_color || [],
            oneColor: result.data.one_color || [],
            twoColors: result.data.two_colors || [],
            moldFees: result.data.mold_fees || [],
            edges: result.data.edges || [],
          });
        }

        setFetchStatus("success");
      } catch (error) {
        setFetchStatus("error");
      }
    };

    fetchData();
  }, []);

  return (
    <div>
      <div role="tablist" className="tabs">
        <input
          type="radio"
          name="tabs"
          className="tab"
          aria-label={t("tabNoColor")}
          value="no color"
          defaultChecked
        />
        <div className="tab-content scroller overflow-x-auto bg-white py-5 sm:p-10">
          {fetchStatus === "success" && (
            <ColorTable data={pricingData.noColor} />
          )}
          {fetchStatus === "loading" && <SkeletonTable rows={12} columns={6} />}
        </div>

        <input
          type="radio"
          name="tabs"
          className="tab"
          aria-label={t("tabOneColor")}
          value="one color"
        />
        <div className="tab-content scroller overflow-x-auto bg-white py-5 sm:p-10">
          {fetchStatus === "success" && (
            <ColorTable data={pricingData.oneColor} />
          )}
          {fetchStatus === "loading" && <SkeletonTable rows={12} columns={6} />}
        </div>

        <input
          type="radio"
          name="tabs"
          className="tab"
          aria-label={t("tabTwoColors")}
          value="two colors"
        />
        <div className="tab-content scroller overflow-x-auto bg-white py-5 sm:p-10">
          {fetchStatus === "success" && (
            <ColorTable data={pricingData.twoColors} />
          )}
          {fetchStatus === "loading" && <SkeletonTable rows={12} columns={6} />}
        </div>

        <input
          type="radio"
          name="tabs"
          className="tab"
          aria-label={t("tabMoldFee")}
          value="mold"
        />
        <div className="tab-content scroller overflow-x-auto bg-white py-5 sm:p-10">
          {fetchStatus === "success" && (
            <MoldTable data={pricingData.moldFees} />
          )}
          {fetchStatus === "loading" && <SkeletonTable rows={12} columns={4} />}
        </div>
      </div>
    </div>
  );
}

/**
 * ColorTable component that displays pricing data grouped by color size.
 *
 * @param {Object} props - Component properties
 * @param {Array} props.data - Array of pricing data objects
 */
const ColorTable = ({ data }) => {
  const { t } = useTranslation();

  // Minimum order quantity
  const minOrderQty = 50;

  // Get all unique sizes
  const sizes = Array.from(new Set(data.map((item) => item.size)));

  // Group data by size and sort by min_quantity
  const groupedBySize = sizes.reduce((acc, size) => {
    const entries = data
      .filter((item) => item.size === size)
      .sort((a, b) => a.min_quantity - b.min_quantity);
    acc[size] = entries;
    return acc;
  }, {});

  // Get all unique display ranges (with last one as "min+")
  let quantityRangesSet = new Set();

  Object.values(groupedBySize).forEach((entries) => {
    entries.forEach((item, index) => {
      const isLast = index === entries.length - 1;
      const label = isLast
        ? `${item.min_quantity}+`
        : `${item.min_quantity}-${item.max_quantity}`;
      quantityRangesSet.add(label);
    });
  });

  const quantityRanges = Array.from(quantityRangesSet);

  // Helper to format price range label
  const formatRangeLabel = (range) => {
    let min, max;
    if (range.includes("+")) {
      min = parseInt(range.replace("+", ""), 10);
      max = Infinity;
    } else {
      [min, max] = range.split("-").map(Number);
    }
    if (max < minOrderQty) return null; // Skip ranges below minimum order quantity
    if (max === minOrderQty) return `${max}`; // Single value
    if (min < minOrderQty && max > minOrderQty)
      return max === Infinity ? `${minOrderQty}+` : `${minOrderQty}-${max}`; // Adjust min to minOrderQty
    return max === Infinity ? `${min}+` : `${min}-${max}`; // Normal range
  };

  // Helper to normalize display range and get price
  const getPrice = (size, displayRange) => {
    let min, max;
    if (displayRange.includes("+")) {
      // "200+" -> min = 200, max = largest possible
      min = parseInt(displayRange.replace("+", ""), 10);
      // Use the actual data to match max
      const entry = data.find(
        (item) => item.size === size && item.min_quantity === min
      );
      return entry ? `${formatCurrency(entry.price)}` : "-";
    } else {
      [min, max] = displayRange.split("-").map(Number);
      const entry = data.find(
        (item) =>
          item.size === size &&
          item.min_quantity === min &&
          item.max_quantity === max
      );
      return entry ? `${formatCurrency(entry.price)}` : "-";
    }
  };

  return (
    <table className="table table-zebra min-w-full text-center text-sm sm:text-base">
      <thead>
        <tr className="border-b-2">
          <th className="p-2 text-subnautical text-sm sm:px-4 sm:py-3 sm:text-base">
            {t("size")}
          </th>
          {quantityRanges.map((range, index) => {
            const label = formatRangeLabel(range);
            if (!label) return null; // Skip ranges below minimum order quantity
            return (
              <th
                key={index}
                className="p-2 text-subnautical text-sm sm:px-4 sm:py-3 sm:text-base"
              >
                {label}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {sizes.map((size, index) => (
          <tr key={index}>
            <td className="p-2 sm:px-4 sm:py-3">
              <Trans
                i18nKey={`sizes.${size}`}
                components={{ strong: <strong /> }}
              />
            </td>
            {quantityRanges.map((range, index) => {
              const label = formatRangeLabel(range);
              if (!label) return null; // Skip ranges below minimum order quantity
              return (
                <td
                  key={index}
                  className="p-2 hover:bg-slate-200 sm:px-4 sm:py-3"
                >
                  {getPrice(size, range)}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
      {isMobile && (
        <tfoot>
          <tr className="border-t-2 border-base-content/5">
            <th className="p-2 text-subnautical text-sm sm:px-4 sm:py-3 sm:text-base">
              {t("size")}
            </th>
            {quantityRanges.map((range, index) => {
              const label = formatRangeLabel(range);
              if (!label) return null; // Skip ranges below minimum order quantity
              return (
                <th
                  key={index}
                  className="p-2 text-subnautical text-sm sm:px-4 sm:py-3 sm:text-base"
                >
                  {label}
                </th>
              );
            })}
          </tr>
        </tfoot>
      )}
    </table>
  );
};

/**
 * MoldTable component that displays mold fees based on size and sides.
 *
 * @param {Object} props - Component properties
 * @param {Array} props.data - Array of mold fee data objects
 */
const MoldTable = ({ data }) => {
  const { t } = useTranslation();

  // Get all unique sizes and sides
  const sizes = Array.from(new Set(data.map((item) => item.size)));
  const sidesOptions = [t("oneSide"), t("twoSides")];

  // Helper to get price based on size and sides
  const getPrice = (size, sides) => {
    const entry = data.find(
      (item) => item.size === size && item.sides === sides
    );
    return entry ? `${formatCurrency(entry.price)}` : "-";
  };

  return (
    <table className="table table-zebra min-w-full text-center text-sm sm:text-base">
      <thead>
        <tr className="border-b-2">
          <th className="p-2 w-60 text-subnautical text-sm sm:px-4 sm:py-3 sm:text-base">
            {t("size")}
          </th>
          {sidesOptions.map((side, index) => (
            <th
              key={index}
              className="p-2 w-60 text-subnautical text-sm sm:px-4 sm:py-3 sm:text-base"
            >
              {side}
            </th>
          ))}
          <th className="p-2 text-subnautical text-sm sm:px-4 sm:py-3 sm:text-base">
            {t("order300+")}
          </th>
        </tr>
      </thead>
      <tbody>
        {sizes.map((size, index) => (
          <tr key={index}>
            <td className="p-2 w-60 sm:px-4 sm:py-3">
              <Trans
                i18nKey={`sizes.${size}`}
                components={{ strong: <strong /> }}
              />
            </td>
            {sidesOptions.map((side) => (
              <td
                key={side}
                className="p-2 w-60 hover:bg-slate-200 sm:px-4 sm:py-3"
              >
                {getPrice(size, side)}
              </td>
            ))}
            <td className="p-2 font-bold uppercase hover:bg-slate-200 sm:px-4 sm:py-3">
              {t("free")}
            </td>
          </tr>
        ))}
      </tbody>
      {isMobile && (
        <tfoot>
          <tr className="border-t-2 border-base-content/5">
            <th className="p-2 w-60 text-subnautical text-sm sm:px-4 sm:py-3 sm:text-base">
              {t("size")}
            </th>
            {sidesOptions.map((side, index) => (
              <th
                key={index}
                className="p-2 w-60 text-subnautical text-sm sm:px-4 sm:py-3 sm:text-base"
              >
                {side}
              </th>
            ))}
            <th className="p-2 text-subnautical text-sm sm:px-4 sm:py-3 sm:text-base">
              {t("order300+")}
            </th>
          </tr>
        </tfoot>
      )}
    </table>
  );
};

/**
 * SkeletonTable component that displays a skeleton loading state for the pricing table.
 *
 * @param {Object} props - Component properties
 * @param {number} props.rows - Number of rows in the skeleton table
 * @param {number} props.columns - Number of columns in the skeleton table
 */
const SkeletonTable = ({ rows = 5, columns = 4 }) => {
  return (
    <div>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div className="w-full py-3 flex gap-2" key={rowIndex}>
          {Array.from({ length: columns }).map((_, colIndex) => (
            <div
              className={`${colIndex === 0 ? "flex-2" : "flex-1"} skeleton h-4`}
              key={colIndex}
            ></div>
          ))}
        </div>
      ))}
    </div>
  );
};
